import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CpuChipIcon,
  CloudIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  BoltIcon,
  ServerIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

const EdgeSpherePage: React.FC = () => {
  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="gradient-bg text-white relative overflow-hidden py-24">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2 rounded-full mb-6">
              <CpuChipIcon className="w-5 h-5" />
              <span className="text-sm font-semibold">QuickReach IoT Platform</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              QuickReach <span className="text-cyan-300">EdgeSphere™</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 max-w-4xl mx-auto mb-4 leading-relaxed">
              Own Your IoT Infrastructure. Stop Paying Per Message.
            </p>
            <p className="text-lg text-gray-300 max-w-4xl mx-auto mb-8">
              Part of the QuickReach Platform—complete control over your IoT devices and data without cloud vendor lock-in or unpredictable costs.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="#pricing"
                className="inline-flex items-center space-x-2 bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
              >
                <span>Calculate Your Savings</span>
              </a>
              <a
                href="#architecture"
                className="inline-flex items-center space-x-2 bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-all duration-200"
              >
                <span>See How It Works</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              The Hidden Cost of Cloud IoT
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-xl shadow-lg border-2 border-red-200"
            >
              <div className="text-5xl mb-4">💸</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Unpredictable Costs</h3>
              <p className="text-gray-700">
                Your Azure bill jumps from $500 to $5,000/month when you increase monitoring frequency. Every message costs money.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-xl shadow-lg border-2 border-orange-200"
            >
              <div className="text-5xl mb-4">🔒</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Data Held Hostage</h3>
              <p className="text-gray-700">
                Your sensitive industrial data sits in Microsoft's cloud. Compliance teams lose sleep.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-xl shadow-lg border-2 border-yellow-200"
            >
              <div className="text-5xl mb-4">⛓️</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Vendor Lock-In</h3>
              <p className="text-gray-700">
                Built everything on Azure IoT? You're stuck with their pricing and roadmap.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              QuickReach EdgeSphere™
            </h2>
            <p className="text-2xl text-gray-600">Private IoT Platform. Unlimited Possibilities.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 bg-cyan-100 rounded-full mb-4">
                <ServerIcon className="w-10 h-10 text-cyan-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">🏢 Your Infrastructure, Your Data</h3>
              <p className="text-gray-700">
                Deploy on-premises or in your private cloud—fully managed inside your QuickReach environment.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
                <CurrencyDollarIcon className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">💰 Fixed Costs, Unlimited Messages</h3>
              <p className="text-gray-700">
                $3,800/year. 1 million messages or 1 billion—same price.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 bg-purple-100 rounded-full mb-4">
                <CloudIcon className="w-10 h-10 text-purple-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">🔓 True Freedom</h3>
              <p className="text-gray-700">
                Use AWS, Google Cloud, Azure, or go fully on-prem. QuickReach workflows automate everything.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Everything You Need. Nothing You Don't.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Device Management",
                icon: <CpuChipIcon className="w-12 h-12 text-cyan-600" />,
                features: [
                  "Registration & authentication via QuickReach IAM",
                  "Real-time status monitoring",
                  "Bulk operations",
                  "X.509 certificate support"
                ]
              },
              {
                title: "Data Collection",
                icon: <ChartBarIcon className="w-12 h-12 text-blue-600" />,
                features: [
                  "MQTT & HTTP protocols",
                  "Time-series database",
                  "Unlimited messages",
                  "Fast queries"
                ]
              },
              {
                title: "Monitoring & Alerts",
                icon: <BoltIcon className="w-12 h-12 text-orange-600" />,
                features: [
                  "Real-time dashboard",
                  "Threshold alerts",
                  "Email/webhook notifications",
                  "Custom alert rules"
                ]
              },
              {
                title: "Multi-Cloud Compatible",
                icon: <CloudIcon className="w-12 h-12 text-purple-600" />,
                features: [
                  "AWS IoT Core compatible",
                  "Google Cloud IoT compatible",
                  "Direct device connection",
                  "Unified QuickReach dashboard"
                ]
              },
              {
                title: "Control & Commands",
                icon: <ServerIcon className="w-12 h-12 text-green-600" />,
                features: [
                  "Remote configuration updates",
                  "Command history tracking",
                  "Bidirectional communication",
                  "Firmware updates"
                ]
              },
              {
                title: "API & Integration",
                icon: <CpuChipIcon className="w-12 h-12 text-red-600" />,
                features: [
                  "REST API access",
                  "Built-in MQTT broker",
                  "Integrates with QuickReach workflows",
                  "Webhook support"
                ]
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-xl shadow-lg"
              >
                <div className="mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <ul className="space-y-2">
                  {feature.features.map((item, i) => (
                    <li key={i} className="flex items-start text-gray-700">
                      <CheckCircleIcon className="w-5 h-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Cost Comparison */}
      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Stop Overpaying for Cloud IoT
            </h2>
          </div>

          <div className="bg-gradient-to-r from-cyan-50 to-blue-50 p-12 rounded-2xl border-2 border-cyan-200">
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-5xl font-bold text-gray-900 mb-2">1,000</div>
                <p className="text-gray-700">Devices sending every 10 seconds</p>
              </div>
              <div>
                <div className="text-5xl font-bold text-red-600 mb-2">$72,000</div>
                <p className="text-gray-700">Azure IoT Hub (5 years)</p>
              </div>
              <div>
                <div className="text-5xl font-bold text-green-600 mb-2">$39,000</div>
                <p className="text-gray-700">EdgeSphere (5 years)</p>
              </div>
            </div>
            
            <div className="mt-8 pt-8 border-t-2 border-cyan-300 text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">Save $33,000</div>
              <p className="text-xl text-gray-700">Plus: Complete data control and no vendor lock-in</p>
            </div>
          </div>

          {/* Pricing Table */}
          <div className="mt-16 grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-cyan-600 to-blue-600 text-white p-8 rounded-xl shadow-2xl border-2 border-cyan-400 transform scale-105"
            >
              <div className="bg-yellow-400 text-gray-900 px-3 py-1 rounded-full text-sm font-bold inline-block mb-4">
                RECOMMENDED
              </div>
              <h3 className="text-2xl font-bold mb-4">Standard License</h3>
              <div className="text-4xl font-bold mb-2">$15,000</div>
              <p className="text-cyan-100 mb-4">One-time license + $3,000/year support</p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-cyan-200 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Unlimited devices & messages</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-cyan-200 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Deploy on-premise or private cloud</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-cyan-200 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Full platform source code</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-cyan-200 mr-2 mt-0.5 flex-shrink-0" />
                  <span>QuickReach integration included</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-cyan-200 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Email & chat support</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-cyan-200 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Software updates & security patches</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-xl shadow-lg border-2 border-gray-200"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Enterprise</h3>
              <div className="text-4xl font-bold text-cyan-600 mb-2">Custom</div>
              <p className="text-gray-600 mb-4">Contact us for pricing</p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Everything in Standard</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Volume licensing discounts</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Custom SLAs & response times</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Dedicated support team</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Custom feature development</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>On-site training & consulting</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Industry Use Cases
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { name: "Manufacturing", icon: "🏭", desc: "Equipment monitoring, predictive maintenance" },
              { name: "Agriculture", icon: "🌾", desc: "Soil monitoring, irrigation automation" },
              { name: "Smart Buildings", icon: "🏢", desc: "HVAC, lighting, energy management" },
              { name: "Healthcare", icon: "🏥", desc: "Patient monitoring, asset tracking" },
              { name: "Fleet Management", icon: "🚚", desc: "Vehicle tracking, diagnostics" },
              { name: "Environmental", icon: "🌍", desc: "Air quality, water monitoring" }
            ].map((useCase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-xl shadow-lg text-center hover:shadow-2xl transition-shadow"
              >
                <div className="text-5xl mb-3">{useCase.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{useCase.name}</h3>
                <p className="text-gray-600">{useCase.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Architecture */}
      <section id="architecture" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Architecture
            </h2>
          </div>

          <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-12 rounded-2xl border-2 border-gray-300">
            <div className="flex flex-col md:flex-row items-center justify-center gap-8 text-center">
              <div className="flex-1">
                <div className="text-4xl mb-2">📡</div>
                <div className="font-bold text-lg">IoT Devices</div>
              </div>
              <div className="text-3xl text-gray-400">→</div>
              <div className="flex-1">
                <div className="text-4xl mb-2">☁️</div>
                <div className="font-bold text-lg">Direct / AWS / Google</div>
              </div>
              <div className="text-3xl text-gray-400">→</div>
              <div className="flex-1 bg-cyan-100 p-4 rounded-xl border-2 border-cyan-400">
                <div className="text-4xl mb-2">🌐</div>
                <div className="font-bold text-lg text-cyan-900">EdgeSphere</div>
                <div className="text-sm text-cyan-700">(Inside QuickReach)</div>
              </div>
              <div className="text-3xl text-gray-400">→</div>
              <div className="flex-1">
                <div className="text-4xl mb-2">📊</div>
                <div className="font-bold text-lg">QuickReach Dashboards</div>
              </div>
            </div>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-8">
            <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Technology Stack</h3>
              <div className="flex flex-wrap gap-3">
                {["MQTT", "Time-Series DB", "REST APIs", "PostgreSQL", "Docker", "TLS/SSL", "RBAC", "Audit Logging"].map((tech) => (
                  <span key={tech} className="bg-cyan-100 text-cyan-800 px-3 py-1 rounded-full text-sm font-semibold">
                    {tech}
                  </span>
                ))}
              </div>
              <p className="mt-4 text-gray-600">
                All integrated into QuickReach Governance, IAM, and Workflow Engine
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Deployment Options</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span><strong>On-Premise:</strong> Full data control in your data center</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span><strong>Private Cloud:</strong> Your AWS/Azure/GCP account</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="w-5 h-5 text-cyan-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span><strong>Hybrid:</strong> Edge + cloud for flexibility</span>
                </li>
              </ul>
              <p className="mt-4 text-gray-600">
                All manageable from QuickReach's infrastructure control panel
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Multi-Cloud Strategy */}
      <section className="py-20 bg-gradient-to-r from-cyan-600 to-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Multi-Cloud Bridge Strategy
          </h2>
          <p className="text-xl mb-8">
            QuickReach EdgeSphere bridges cloud IoT (AWS/Google) into your QuickReach Platform, keeping your data sovereign while leveraging cloud connectivity.
          </p>
          <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl">
            <p className="text-lg">
              Connect devices through AWS IoT Core or Google Cloud IoT, but process and store data in your own infrastructure. Best of both worlds: cloud convenience + data sovereignty.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Take Control of Your IoT with QuickReach EdgeSphere
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Stop overpaying for cloud IoT. Own your infrastructure, control your data, and scale without limits.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="mailto:sales@blastasia.com"
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:from-cyan-700 hover:to-blue-700 transition-all"
            >
              <span>Calculate Savings</span>
            </a>
            <a
              href="mailto:sales@blastasia.com"
              className="inline-flex items-center space-x-2 bg-gray-900 text-white px-8 py-4 rounded-lg font-semibold hover:bg-gray-800 transition-colors"
            >
              <span>Schedule Demo</span>
            </a>
            <Link
              to="/faq"
              className="inline-flex items-center space-x-2 bg-transparent border-2 border-gray-900 text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-50 transition-all"
            >
              <span>Technical FAQs</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EdgeSpherePage;

